/**
 * Category presentation — the ONE place per-category Tailwind class strings
 * live. Consumers (`CategoryTag`, `TxnFormModal`'s chip picker, and any P3+
 * invoice-status surface that reuses the prototype palette) import from here.
 *
 * Tailwind v4 JIT scans LITERAL class strings, so we cannot template-derive
 * (`bg-[${hex}]`) at runtime — each colour ships as a hand-mirrored literal
 * below. The hex source-of-truth stays in `api/taxonomy.ts`
 * (`CATEGORY_TAXONOMY[*].color`). To prevent the two from drifting, the
 * companion test (`category-meta.test.ts`) asserts every `swatchClass` and
 * `chipSelectedClass` contains the corresponding taxonomy hex. When the
 * palette changes:
 *   1. update `taxonomy.ts`
 *   2. update this file's matching row
 *   3. the test enforces (1) and (2) stay aligned
 *
 * Class composition:
 *   - `swatchClass`: a single `bg-[…]` for the 10×10 dot used in tags / chips.
 *   - `chipSelectedClass`: per-category border + 10 %-alpha background + text
 *     in the same hex, applied to the SELECTED state of a chip filter. The
 *     `1a` hex-alpha suffix (`#1b8a4d1a`) gives ~10 % opacity — a subtle tonal
 *     fill that lets the 100 % text colour read cleanly against the chip.
 *
 * `CHIP_UNSELECTED_CLASS` is one neutral string shared across all categories
 * — kept here so consumers don't have to duplicate the fallback styling.
 */

import type { CategoryId } from '../api/contracts'

export interface CategoryMeta {
  /** Solid swatch — the dot used in CategoryTag + the chip swatch. */
  swatchClass: string
  /**
   * Per-category border + 10 %-alpha background + matching text colour,
   * applied to the SELECTED state of a category chip filter. Composed of
   * three literal arbitrary-value Tailwind classes so the JIT picks all
   * three up.
   */
  chipSelectedClass: string
}

export const CATEGORY_META: Record<CategoryId, CategoryMeta> = {
  'c-prod': {
    swatchClass: 'bg-[#1b8a4d]',
    chipSelectedClass: 'border-[#1b8a4d] bg-[#1b8a4d1a] text-[#1b8a4d]',
  },
  'c-srv': {
    swatchClass: 'bg-[#2272d6]',
    chipSelectedClass: 'border-[#2272d6] bg-[#2272d61a] text-[#2272d6]',
  },
  'c-other': {
    swatchClass: 'bg-[#9b6bd1]',
    chipSelectedClass: 'border-[#9b6bd1] bg-[#9b6bd11a] text-[#9b6bd1]',
  },
  'c-loan': {
    swatchClass: 'bg-[#d9a635]',
    chipSelectedClass: 'border-[#d9a635] bg-[#d9a6351a] text-[#d9a635]',
  },
  'e-mat': {
    swatchClass: 'bg-[#d9533a]',
    chipSelectedClass: 'border-[#d9533a] bg-[#d9533a1a] text-[#d9533a]',
  },
  'e-trans': {
    swatchClass: 'bg-[#ef8a17]',
    chipSelectedClass: 'border-[#ef8a17] bg-[#ef8a171a] text-[#ef8a17]',
  },
  'e-zp': {
    swatchClass: 'bg-[#b85ec7]',
    chipSelectedClass: 'border-[#b85ec7] bg-[#b85ec71a] text-[#b85ec7]',
  },
  'e-util': {
    swatchClass: 'bg-[#1f7ad1]',
    chipSelectedClass: 'border-[#1f7ad1] bg-[#1f7ad11a] text-[#1f7ad1]',
  },
  'e-rent': {
    swatchClass: 'bg-[#5a6b88]',
    chipSelectedClass: 'border-[#5a6b88] bg-[#5a6b881a] text-[#5a6b88]',
  },
  'e-tax': {
    swatchClass: 'bg-[#0e1a2c]',
    chipSelectedClass: 'border-[#0e1a2c] bg-[#0e1a2c1a] text-[#0e1a2c]',
  },
  'e-other': {
    swatchClass: 'bg-[#94a3b8]',
    chipSelectedClass: 'border-[#94a3b8] bg-[#94a3b81a] text-[#94a3b8]',
  },
}

/** Neutral fallback for unselected chip state — shared across all categories. */
export const CHIP_UNSELECTED_CLASS =
  'border-[color:var(--input-border)] bg-white text-(--text-default)'

/** Swatch fallback for an unknown `categoryId` (defense — Zod catches first). */
export const UNKNOWN_SWATCH_CLASS = 'bg-[#94a3b8]'
